var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useMemo, useState } from 'react';
import { Box, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, } from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { tooltipClasses } from '@mui/material/Tooltip';
import { TableRoot, tableClasses } from './CommonTableStyles';
import { api_service } from '../../api/api_service';
import { baseColors } from '../../Themes/colors';
import { parseStatusReasonUdf } from '../../utils/statusReasonUdf';
var StatusInfoTooltip = styled(function (_a) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (React.createElement(Tooltip, __assign({}, props, { arrow: true, classes: { popper: className }, placement: "right-start" })));
})(function () {
    var _a;
    return (_a = {},
        _a["& .".concat(tooltipClasses.tooltip)] = {
            backgroundColor: baseColors.bluePreset[10],
            color: baseColors.fullWhite,
            borderRadius: 8,
            boxShadow: '0 10px 24px rgba(0,0,0,0.24)',
            padding: '14px 18px',
            minWidth: 250,
            maxWidth: 360,
            maxHeight: '60vh',
            overflowY: 'auto',
        },
        _a["& .".concat(tooltipClasses.arrow)] = {
            color: baseColors.bluePreset[10],
        },
        _a.zIndex = 1700,
        _a);
});
function CommonTable(props) {
    var _a;
    var columns = props.columns, data = props.data, _b = props.loading, loading = _b === void 0 ? false : _b, _c = props.loadingMessage, loadingMessage = _c === void 0 ? 'Loading...' : _c, _d = props.emptyMessage, emptyMessage = _d === void 0 ? 'No records found.' : _d, onRowClick = props.onRowClick, keyExtractor = props.keyExtractor, paginationSlot = props.paginationSlot, _e = props.stickyHeader, stickyHeader = _e === void 0 ? true : _e, height = props.height, onSortChange = props.onSortChange, currentSortKey = props.currentSortKey, currentSortDir = props.currentSortDir, moduleName = props.moduleName, ariaLabel = props["aria-label"], testId = props["data-testid"];
    var showInfoIcon = moduleName === 'Application Management';
    var colSpan = columns.length;
    var isClickable = typeof onRowClick === 'function';
    var _f = useState(null), internalSortKey = _f[0], setInternalSortKey = _f[1];
    var _g = useState('asc'), internalSortDir = _g[0], setInternalSortDir = _g[1];
    var sortKey = currentSortKey !== null && currentSortKey !== void 0 ? currentSortKey : internalSortKey;
    var sortDir = currentSortDir !== null && currentSortDir !== void 0 ? currentSortDir : internalSortDir;
    var isExternalSorting = typeof onSortChange === 'function';
    var _h = useState({ creditConditions: [], statusReasons: [], statusReasonUDF: null }), adjudicationData = _h[0], setAdjudicationData = _h[1];
    var _j = useState(null), activeInfoRowId = _j[0], setActiveInfoRowId = _j[1];
    var _k = useState(false), isInfoTooltipLoading = _k[0], setIsInfoTooltipLoading = _k[1];
    var handleInfoIconClick = useCallback(function (e, row) {
        e.stopPropagation();
        var txAspireOId = row._original.TxAspireOId;
        setAdjudicationData({ creditConditions: [], statusReasons: [], statusReasonUDF: null });
        setActiveInfoRowId(txAspireOId);
        setIsInfoTooltipLoading(true);
        api_service.get({
            endPoint: api_service.api_urls.api_urls.GET_CONTRACT_ADJUDICATION,
            payLoad: { TxAspireOId: txAspireOId, Type: 'STATUS_REASONS' },
            showLoader: false,
        }).then(function (response) {
            var _a, _b, _c, _d, _e, _f;
            setAdjudicationData({
                creditConditions: ((_b = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.CreditConditions) || [],
                statusReasons: ((_d = (_c = response === null || response === void 0 ? void 0 : response.data) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.StatusReasons) || [],
                statusReasonUDF: ((_f = (_e = response === null || response === void 0 ? void 0 : response.data) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.StatusReasonUDF) || ''
            });
        }).catch(function (error) {
            console.error('Contract adjudication error:', error);
        }).finally(function () {
            setIsInfoTooltipLoading(false);
        });
    }, []);
    var renderTooltipContent = function () {
        if (isInfoTooltipLoading) {
            return (React.createElement(Typography, { sx: { fontSize: 11, lineHeight: '17px', color: baseColors.fullWhite } }, "Loading..."));
        }
        return (React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 } },
            adjudicationData.creditConditions.length > 0 && (React.createElement(Box, null,
                React.createElement(Typography, { sx: { fontSize: 12, fontWeight: 700, lineHeight: '18px', color: baseColors.fullWhite } }, "Credit Conditions:"),
                React.createElement("ul", { style: { margin: 0, paddingLeft: 16 } }, adjudicationData.creditConditions.map(function (item) { return (React.createElement("li", { key: String(item) },
                    React.createElement(Typography, { sx: { fontSize: 11, lineHeight: '17px', color: baseColors.fullWhite } }, item))); })))),
            adjudicationData.statusReasons.length > 0 && (React.createElement(Box, null,
                React.createElement(Typography, { sx: { fontSize: 12, fontWeight: 700, lineHeight: '18px', color: baseColors.fullWhite } }, "Status Reasons:"),
                React.createElement("ul", { style: { margin: 0, paddingLeft: 16 } }, adjudicationData.statusReasons.map(function (item) { return (React.createElement("li", { key: String(item) },
                    React.createElement(Typography, { sx: { fontSize: 11, lineHeight: '17px', color: baseColors.fullWhite } }, item))); })))),
            adjudicationData.statusReasonUDF && (React.createElement(Box, null,
                React.createElement(Typography, { sx: { fontSize: 12, fontWeight: 700, lineHeight: '18px', color: baseColors.fullWhite } }, "Status Reason UDF:"),
                React.createElement("ul", { style: { margin: 0, paddingLeft: 16 } }, parseStatusReasonUdf(adjudicationData.statusReasonUDF).map(function (reason, index) { return (React.createElement("li", { key: "".concat(reason, "-").concat(index), style: { fontSize: 11, lineHeight: '17px', color: baseColors.fullWhite } }, reason)); }))))));
    };
    var sortedData = useMemo(function () {
        if (isExternalSorting)
            return data;
        if (!sortKey)
            return data;
        return __spreadArray([], data, true).sort(function (a, b) {
            var _a, _b;
            var aVal = (_a = a[sortKey]) !== null && _a !== void 0 ? _a : '';
            var bVal = (_b = b[sortKey]) !== null && _b !== void 0 ? _b : '';
            var cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [data, isExternalSorting, sortKey, sortDir]);
    var getStickyCellSx = function (col, isHeader) { return (col.sticky === 'right'
        ? {
            position: 'sticky',
            right: 0,
            zIndex: isHeader ? 3 : 2,
            backgroundColor: baseColors.fullWhite,
            boxShadow: '-2px 0 4px rgba(0,0,0,0.06)',
        }
        : undefined); };
    var handleSort = function (column) {
        var _a;
        var nextSortKey = (_a = column.sortKey) !== null && _a !== void 0 ? _a : column.key;
        if (sortKey === nextSortKey) {
            var nextSortDir = sortDir === 'asc' ? 'desc' : 'asc';
            if (isExternalSorting) {
                onSortChange === null || onSortChange === void 0 ? void 0 : onSortChange(nextSortKey, nextSortDir);
                return;
            }
            setInternalSortDir(nextSortDir);
        }
        else {
            if (isExternalSorting) {
                onSortChange === null || onSortChange === void 0 ? void 0 : onSortChange(nextSortKey, 'asc');
                return;
            }
            setInternalSortKey(nextSortKey);
            setInternalSortDir('asc');
        }
    };
    var closeActiveInfoTooltip = function () {
        setActiveInfoRowId(null);
        setIsInfoTooltipLoading(false);
    };
    var renderStatusInfoCell = function (row, renderedContent) { return (React.createElement(Box, { sx: { display: 'inline-flex', alignItems: 'center', gap: 0.75 } },
        renderedContent,
        React.createElement(StatusInfoTooltip, { title: activeInfoRowId === row._original.TxAspireOId ? renderTooltipContent() : '', open: activeInfoRowId === row._original.TxAspireOId && (isInfoTooltipLoading || adjudicationData.creditConditions.length > 0 || adjudicationData.statusReasons.length > 0 || !!adjudicationData.statusReasonUDF), onClose: closeActiveInfoTooltip },
            React.createElement(IconButton, { size: "small", onClick: function (e) { return handleInfoIconClick(e, row); } },
                React.createElement(InfoOutlinedIcon, { sx: { fontSize: 18, color: baseColors.bluePreset[40] } }))))); };
    var renderTableBody = function () {
        if (loading) {
            return (React.createElement(TableRow, { className: tableClasses.row },
                React.createElement(TableCell, { className: "".concat(tableClasses.cell, " ").concat(tableClasses.empty), colSpan: colSpan },
                    React.createElement(Typography, { fontSize: 14, fontWeight: 600, color: "inherit", paddingTop: '40px' }, loadingMessage))));
        }
        if (!(sortedData === null || sortedData === void 0 ? void 0 : sortedData.length)) {
            return (React.createElement(TableRow, { className: tableClasses.row },
                React.createElement(TableCell, { className: "".concat(tableClasses.cell, " ").concat(tableClasses.empty), colSpan: colSpan },
                    React.createElement(Typography, { fontSize: 14, fontWeight: 600, color: "inherit", paddingTop: '40px' }, emptyMessage))));
        }
        return sortedData.map(function (row, index) {
            var rowClassName = isClickable
                ? "".concat(tableClasses.row, " ").concat(tableClasses.rowClickable)
                : tableClasses.row;
            return (React.createElement(TableRow, { key: keyExtractor(row, index), className: rowClassName, onClick: function () { return isClickable && (onRowClick === null || onRowClick === void 0 ? void 0 : onRowClick(row)); } }, columns.map(function (col) {
                var _a;
                var cellValue = row[col.key];
                var renderedContent = col.render
                    ? col.render(cellValue, row, index)
                    : (cellValue !== null && cellValue !== void 0 ? cellValue : '—');
                var showStatusInfoIcon = showInfoIcon
                    && col.key === 'status'
                    && (row.status === '56 - Docs Back Missing Info' || row.status === '39 - Withdrawn');
                return (React.createElement(TableCell, { key: col.key, className: tableClasses.cell, align: (_a = col.align) !== null && _a !== void 0 ? _a : 'left', sx: getStickyCellSx(col, false) }, showStatusInfoIcon ? renderStatusInfoCell(row, renderedContent) : renderedContent));
            })));
        });
    };
    return (React.createElement(TableRoot, { role: "region", "aria-label": ariaLabel, "data-testid": testId, sx: __assign({ height: height, paddingTop: moduleName === "Resubmit Application" ? '10px' : '0' }, (moduleName === "Documents Management" ? (_a = {},
            _a["& .".concat(tableClasses.cell)] = { padding: '7px 12px' },
            _a) : {})) },
        React.createElement(TableContainer, null,
            React.createElement(Table, { stickyHeader: stickyHeader },
                React.createElement(TableHead, null,
                    React.createElement(TableRow, { className: tableClasses.row }, columns.map(function (col) {
                        var _a;
                        return (React.createElement(TableCell, { key: col.key, className: "".concat(tableClasses.cell, " ").concat(tableClasses.cellHead), align: (_a = col.align) !== null && _a !== void 0 ? _a : 'left', style: { width: col.width, cursor: col.sortable ? 'pointer' : 'default' }, onClick: col.sortable ? function () { return handleSort(col); } : undefined, sx: getStickyCellSx(col, true) }, col.sortable ? (React.createElement(Box, { sx: { display: 'inline-flex', alignItems: 'center', gap: 0.5 } },
                            col.header,
                            React.createElement("span", { className: "icon-import_export", style: { fontSize: 16, lineHeight: 1 } }))) : col.header));
                    }))),
                React.createElement(TableBody, null, renderTableBody()))),
        paginationSlot));
}
export default React.memo(CommonTable);
